import { useTranslation } from 'react-i18next'
import { fmtMoney } from '@/features/shared/format'
import { bordroBreakdown } from '../../lib/bordroActions'
import type { BordroSatir } from '../../types'

export function BordroSummaryBody({ row }: { row: BordroSatir }) {
  const { t } = useTranslation()
  const total = t('common.total', { defaultValue: 'Toplam' })
  const b = bordroBreakdown(row)

  return (
    <div className="space-y-4">
      <div>
        <div className="text-muted-foreground mb-1 text-xs font-medium tracking-wide uppercase">
          {t('hr.personel.ucret.kazanclar')}
        </div>
        {b.kazanc.map((k) => (
          <div key={k.label} className="flex justify-between gap-2 py-1 text-sm">
            <span className="text-muted-foreground">{k.label}</span>
            <span className="tabular-nums">{fmtMoney(k.amount)}</span>
          </div>
        ))}
        <div className="mt-1 flex justify-between border-t pt-2 text-sm font-semibold">
          <span>{total}</span>
          <span className="tabular-nums">{fmtMoney(b.kazancToplam)}</span>
        </div>
      </div>

      <div>
        <div className="text-muted-foreground mb-1 text-xs font-medium tracking-wide uppercase">
          {t('hr.personel.ucret.kesintiler')}
        </div>
        {b.kesinti.map((k) => (
          <div key={k.label} className="flex justify-between gap-2 py-1 text-sm">
            <span className="text-muted-foreground">{k.label}</span>
            <span className="text-destructive tabular-nums">{fmtMoney(k.amount)}</span>
          </div>
        ))}
        <div className="text-destructive mt-1 flex justify-between border-t pt-2 text-sm font-semibold">
          <span>{total}</span>
          <span className="tabular-nums">{fmtMoney(row.kesinti)}</span>
        </div>
      </div>

      <div className="bg-success/10 text-success rounded-lg px-3 py-2.5 text-sm font-semibold">
        <div className="flex justify-between gap-2">
          <span>{t('hr.personel.ucret.net')}</span>
          <span className="tabular-nums">{fmtMoney(row.net)}</span>
        </div>
      </div>

      {b.istisna.length ? (
        <Lines title={t('hr.personel.bordro.dokum.istisnaTitle')} rows={b.istisna} tone="text-success" />
      ) : null}

      {b.isverenMaliyet ? (
        <div>
          <Lines title={t('hr.personel.bordro.dokum.isverenTitle')} rows={b.isveren} />
          <div className="mt-1 flex justify-between border-t pt-2 text-sm font-semibold">
            <span>{t('hr.personel.bordro.dokum.isverenMaliyet')}</span>
            <span className="tabular-nums">{fmtMoney(b.isverenMaliyet)}</span>
          </div>
        </div>
      ) : (
        <p className="bg-warning/10 text-warning-strong rounded-lg px-3 py-2 text-xs">
          {row.blokeNedeni || t('hr.personel.bordro.dokum.missing')}
        </p>
      )}

      {b.matrah ? (
        <p className="text-muted-foreground text-xs">
          {t('hr.personel.bordro.dokum.matrahLine', {
            sgk: fmtMoney(b.matrah.sgk),
            gv: fmtMoney(b.matrah.gv),
            kum: fmtMoney(b.matrah.kumulatif),
          })}
        </p>
      ) : null}
    </div>
  )
}

function Lines({ title, rows, tone }: { title: string; rows: { label: string; amount: string }[]; tone?: string }) {
  return (
    <div>
      <div className="text-muted-foreground mb-1 text-xs font-medium tracking-wide uppercase">{title}</div>
      {rows.map((k) => (
        <div key={k.label} className="flex justify-between gap-2 py-1 text-sm">
          <span className="text-muted-foreground">{k.label}</span>
          <span className={tone ? `tabular-nums ${tone}` : 'tabular-nums'}>{fmtMoney(k.amount)}</span>
        </div>
      ))}
    </div>
  )
}
